import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";

const faqs = [
  {
    q: "What is the ClusterClear wristband?",
    a: "ClusterClear is a smart NFC wristband that lets you share your business, social media, contact details and other links with a simple tap. Instead of searching for you online, someone can simply tap your wristband with their phone.",
  },
  {
    q: "How does the wristband work?",
    a: "Your wristband contains an NFC chip. When someone taps their NFC-enabled phone against it, your ClusterClear page opens automatically in their browser. No app is required.",
  },
  {
    q: "Does it work with iPhone and Android?",
    a: "Yes. ClusterClear is designed to work with modern NFC-enabled iPhones and Android phones. The person tapping your wristband does not need a ClusterClear account or app.",
  },
  {
    q: "Does the wristband need charging?",
    a: "No. The wristband does not contain a battery, so there is nothing to charge. The NFC chip is powered by the phone when it is tapped.",
  },
  {
    q: "Can I change the links on my wristband?",
    a: "Yes. Your wristband points to your ClusterClear page, so you can update the information on your page without replacing the wristband. Add or change your social media, website, contact details and other links whenever you need to.",
  },
  {
    q: "What can I put on my ClusterClear page?",
    a: "You can add the links that matter most to you, including Instagram, TikTok, WhatsApp, your website, phone number, email, booking links and other important business or social profiles.",
  },
  {
    q: "Is the wristband water resistant?",
    a: "Yes. The wristband is designed for everyday wear and is water resistant. You can keep it on during normal daily activities without needing to remove it.",
  },
  {
    q: "Do I need to install an app?",
    a: "No. There is no app that your customers need to install. They simply tap the wristband with a compatible phone and your ClusterClear page opens in their browser.",
  },
  {
    q: "How do I set up my wristband?",
    a: "Once you receive your wristband, you can access your ClusterClear account and customise your page with your business information, social links and contact details. Your wristband is then ready to share.",
  },
  {
    q: "Can I use my wristband for more than just my business?",
    a: "Absolutely. ClusterClear can be used by business owners, creators, barbers, DJs, MCs, freelancers, professionals and anyone who wants to make sharing their online presence easier.",
  },
  {
    q: "What happens if I change my Instagram or TikTok username?",
    a: "You can simply update the link on your ClusterClear page. Your physical wristband does not need to be replaced.",
  },
  {
    q: "How long will my wristband last?",
    a: "The wristband is designed for everyday use. With no battery or electronics that need charging, there is no charging cycle to worry about. Its lifespan will mainly depend on normal wear and tear.",
  },
];

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" style={styles.page}>
      <div style={styles.container}>

        {/* Header */}
        <motion.div
          style={styles.header}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{
            duration: 0.8,
            ease: [0.22, 1, 0.36, 1],
          }}
        >
          <p style={styles.eyebrow}>
            FAQ
          </p>

          <h2 style={styles.title}>
            Questions?
            <br />
            <span style={styles.titleLight}>
              We've got answers.
            </span>
          </h2>

          <p style={styles.subtitle}>
            Everything you need to know about your ClusterClear wristband.
          </p>
        </motion.div>


        {/* FAQ List */}
        <motion.div
          style={styles.list}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={{
            hidden: {},
            visible: {
              transition: {
                staggerChildren: 0.06,
              },
            },
          }}
        >

          {faqs.map((item, index) => (
            <motion.div
              key={index}
              variants={{
                hidden: {
                  opacity: 0,
                  y: 15,
                },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.5,
                    ease: [0.22, 1, 0.36, 1],
                  },
                },
              }}
              style={styles.item}
            >

              <button
                onClick={() => toggle(index)}
                style={styles.question}
                aria-expanded={openIndex === index}
              >
                <span>{item.q}</span>

                <motion.span
                  animate={{
                    rotate: openIndex === index ? 45 : 0,
                  }}
                  transition={{
                    duration: 0.25,
                  }}
                  style={styles.icon}
                >
                  <Plus size={22} strokeWidth={1.7} />
                </motion.span>
              </button>


              <AnimatePresence initial={false}>
                {openIndex === index && (
                  <motion.div
                    initial={{
                      height: 0,
                      opacity: 0,
                    }}
                    animate={{
                      height: "auto",
                      opacity: 1,
                    }}
                    exit={{
                      height: 0,
                      opacity: 0,
                    }}
                    transition={{
                      height: {
                        duration: 0.35,
                        ease: [0.22, 1, 0.36, 1],
                      },
                      opacity: {
                        duration: 0.2,
                      },
                    }}
                    style={styles.answerWrapper}
                  >
                    <p style={styles.answer}>
                      {item.a}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

            </motion.div>
          ))}

        </motion.div>


        {/* Bottom CTA */}
        <motion.div
          style={styles.bottom}
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{
            duration: 0.7,
            delay: 0.2,
          }}
        >
          <p style={styles.bottomText}>
            Still have questions?
          </p>

          <a
            href="#contact"
            style={styles.bottomLink}
          >
            Get in touch →
          </a>
        </motion.div>

      </div>
    </section>
  );
};


const styles = {

  page: {
    background: "#ffffff",
    color: "#111111",
    padding: "150px 20px",
  },

  container: {
    maxWidth: "850px",
    margin: "0 auto",
  },

  header: {
    textAlign: "center",
    marginBottom: "80px",
  },

  eyebrow: {
    fontSize: "11px",
    letterSpacing: "0.18em",
    fontWeight: "600",
    color: "#888",
    marginBottom: "22px",
  },

  title: {
    margin: 0,
    fontSize: "clamp(3rem, 6vw, 5rem)",
    lineHeight: "1",
    letterSpacing: "-0.06em",
    fontWeight: "700",
  },

  titleLight: {
    color: "#777",
    fontWeight: "600",
  },

  subtitle: {
    maxWidth: "500px",
    margin: "28px auto 0",
    fontSize: "1.05rem",
    lineHeight: "1.7",
    color: "#666",
  },

  list: {
    borderTop: "1px solid #e8e8e8",
  },

  item: {
    borderBottom: "1px solid #e8e8e8",
  },

  question: {
    width: "100%",
    border: "none",
    background: "transparent",
    padding: "28px 4px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "30px",
    cursor: "pointer",
    textAlign: "left",
    fontSize: "1.05rem",
    fontWeight: "600",
    color: "#111",
  },

  icon: {
    minWidth: "32px",
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    background: "#f5f5f5",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  answerWrapper: {
    overflow: "hidden",
  },

  answer: {
    maxWidth: "680px",
    margin: "0 0 28px",
    paddingRight: "50px",
    fontSize: "0.98rem",
    lineHeight: "1.75",
    color: "#666",
  },

  bottom: {
    textAlign: "center",
    marginTop: "70px",
  },

  bottomText: {
    color: "#888",
    margin: "0 0 10px",
    fontSize: "0.95rem",
  },

  bottomLink: {
    color: "#6355ff",
    textDecoration: "none",
    fontWeight: "600",
    fontSize: "0.95rem",
  },
};

export default FAQ;